import { useLazyQuery } from '@apollo/client';

import { SKIP_AUTH_CONTEXT_KEY } from '@/constants';
import { useSeekerState } from '@/components/AppState';
import {
  getCaregiverCountForJob,
  getCaregiverCountForJobVariables,
} from '@/__generated__/getCaregiverCountForJob';
import { ChildCareSearchCriteria, ServiceType, SubServiceType } from '@/__generated__/globalTypes';
import { useEffect } from 'react';
import { GET_CAREGIVER_COUNT_FOR_JOB } from '../request/GQL';

const minimumProvidersToDisplay = 15;

export interface GetProviderCountResponse {
  numOfProviders: number;
  displayProviderMessage: boolean;
}

export default function useProviderCount(
  serviceType: ServiceType,
  childCareSearchCriteria?: ChildCareSearchCriteria,
  subServiceType?: SubServiceType | null,
  // Used when the zip code is entered on the same screen
  validZipcode?: boolean
): GetProviderCountResponse {
  const { zipcode } = useSeekerState();
  let numOfProviders = 0;

  // Skip making getCaregiverCountForJob call for Daycare
  if (subServiceType === SubServiceType.DAY_CARE) {
    return {
      numOfProviders,
      displayProviderMessage: false,
    };
  }

  const [getCaregiverCount, { loading, data, error }] = useLazyQuery<
    getCaregiverCountForJob,
    getCaregiverCountForJobVariables
  >(GET_CAREGIVER_COUNT_FOR_JOB, {
    context: { [SKIP_AUTH_CONTEXT_KEY]: true },
  });

  useEffect(() => {
    if (validZipcode !== false && zipcode?.length === 5) {
      getCaregiverCount({ variables: { zipcode, serviceType, childCareSearchCriteria } });
    }
  }, [validZipcode, zipcode]);

  const providersNearbySuccessful = !error && Number.isInteger(data?.getCaregiverCountForJob.count);

  if (providersNearbySuccessful && data) {
    numOfProviders = data.getCaregiverCountForJob.count as number;
  }

  return {
    numOfProviders,
    displayProviderMessage: !error && !loading && numOfProviders > minimumProvidersToDisplay,
  };
}
